import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Cake } from '@mui/icons-material';
import { useFirebaseContext } from '../lib/firebase-context';
import {
  BirthdayMonth,
  MONTH_NAMES,
  countMalformedBirthdates,
  defaultBirthdayMonth,
  getBirthdaysInMonth,
} from '../lib/birthdays';

// Single-purpose page that lists every live attendee on the active event
// whose birthdate falls in the selected month. Read-only; no audited
// writes, no s3LabelUrl invalidation, no activity-log entries.
//
// The month dropdown defaults to the active event's start month (falling
// back to the current calendar month if the event has no startDate). All
// data comes from `useFirebaseContext()` — no new Firestore listeners.

const MONTHS: BirthdayMonth[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const BirthdaysPage = () => {
  const { activeEvent, attendees, groups } = useFirebaseContext();

  const eventStart = useMemo(
    () => activeEvent?.startDate?.toDate(),
    [activeEvent?.startDate],
  );

  const [month, setMonth] = useState<BirthdayMonth>(() =>
    defaultBirthdayMonth(eventStart),
  );

  // Group lookup: ref -> "grade · groupName" string. Keeps the row render
  // O(1) per attendee instead of scanning the groups array each time.
  const groupLabelByRef = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) {
      map.set(g.ref, `${g.grade} · ${g.groupName}`);
    }
    return map;
  }, [groups]);

  const today = new Date();
  const isCurrentMonth = today.getMonth() + 1 === month;
  const todayDay = today.getDate();
  const referenceYear = today.getFullYear();

  const rows = useMemo(
    () => getBirthdaysInMonth(attendees, month, referenceYear),
    [attendees, month, referenceYear],
  );

  const malformedCount = useMemo(
    () => countMalformedBirthdates(attendees),
    [attendees],
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant='h5'>
          Birthdays in {MONTH_NAMES[month - 1]}
        </Typography>
        <FormControl size='small' sx={{ minWidth: 160 }}>
          <InputLabel id='birthday-month-label'>Month</InputLabel>
          <Select
            labelId='birthday-month-label'
            label='Month'
            value={month}
            onChange={(e) => setMonth(e.target.value as BirthdayMonth)}
          >
            {MONTHS.map((m) => (
              <MenuItem key={m} value={m}>
                {MONTH_NAMES[m - 1]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!activeEvent && (
        <Alert severity='info' sx={{ mb: 2 }}>
          No active event. Birthdays will appear here once an event is
          activated.
        </Alert>
      )}

      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        {rows.length === 0
          ? `No attendees with ${MONTH_NAMES[month - 1]} birthdays.`
          : `${rows.length} attendee${rows.length === 1 ? '' : 's'} with ${MONTH_NAMES[month - 1]} birthday${rows.length === 1 ? '' : 's'}.`}
      </Typography>

      {rows.length > 0 && (
        <TableContainer component={Paper} variant='outlined'>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}>Day</TableCell>
                <TableCell>Name</TableCell>
                <TableCell sx={{ width: 110 }}>Turning</TableCell>
                <TableCell sx={{ width: 80 }}>Grade</TableCell>
                <TableCell>Group</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const isToday = isCurrentMonth && row.day === todayDay;
                const groupLabel = row.attendee.groupRef
                  ? groupLabelByRef.get(row.attendee.groupRef)
                  : undefined;
                const dayStr = `${String(month).padStart(2, '0')}/${String(row.day).padStart(2, '0')}`;
                return (
                  <TableRow
                    key={row.attendee.ref}
                    hover
                    sx={{
                      height: 48,
                      ...(isToday && {
                        backgroundColor: 'action.selected',
                      }),
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        {isToday && (
                          <Tooltip title="Today's birthday" placement='top'>
                            <Cake fontSize='small' color='secondary' />
                          </Tooltip>
                        )}
                        {dayStr}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.attendee.first_name} {row.attendee.last_name}
                    </TableCell>
                    <TableCell>turns {row.ageTurning}</TableCell>
                    <TableCell>
                      {row.attendee.grade_completed || (
                        <Typography variant='body2' color='text.secondary'>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {groupLabel ?? (
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ fontStyle: 'italic' }}
                        >
                          (Unassigned)
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {malformedCount > 0 && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mt: 2 }}
        >
          {malformedCount} attendee{malformedCount === 1 ? ' has' : 's have'} a
          birthdate stored in an unrecognised format and may be missing from
          this list.
        </Typography>
      )}
    </>
  );
};

export default BirthdaysPage;
